import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Play, Pause, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface SimpleAudioFallbackProps {
  audioUrl: string;
  sessionTitle?: string;
  onEnded?: () => void;
}

const SimpleAudioFallback: React.FC<SimpleAudioFallbackProps> = ({
  audioUrl,
  sessionTitle = 'Audio Session',
  onEnded
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setHasError(false);
      console.log('✅ Fallback audio loaded successfully');
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      onEnded?.();
    };

    const handleError = (e: Event) => {
      console.error('❌ Fallback audio error:', e);
      setHasError(true);
      setIsLoading(false);
      setIsPlaying(false);
    };

    const handleCanPlayThrough = () => {
      setIsLoading(false);
      console.log('✅ Fallback audio can play through');
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplaythrough', handleCanPlayThrough);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
    };
  }, [onEnded]);

  const handlePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (isPlaying) {
        audio.pause();
      } else {
        setIsLoading(true);
        await audio.play();
      }
    } catch (error) {
      console.error('❌ Fallback playback failed:', error);
      setIsLoading(false);
      setHasError(true);
      toast({
        title: "Playback Failed",
        description: "Unable to play audio. Please check your connection.",
        variant: "destructive"
      });
    }
  };

  const handleRetry = () => {
    const audio = audioRef.current;
    if (!audio) return;

    setHasError(false);
    setIsLoading(true);
    audio.load();
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Card className="w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-orange-200">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold text-orange-700">
          🔧 Fallback Player: {sessionTitle}
        </CardTitle>
        <div className="text-xs text-orange-600">
          Using simple HTML5 audio player
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
          crossOrigin={audioUrl.startsWith('http') ? 'anonymous' : undefined}
          style={{ display: 'none' }}
        />

        {/* Progress */}
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={handlePlayPause}
            disabled={!audioUrl || isLoading || hasError}
            className="h-12 w-12 rounded-full"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
            ) : isPlaying ? (
              <Pause className="h-5 w-5" />
            ) : (
              <Play className="h-5 w-5" />
            )}
          </Button>

          {hasError && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
              disabled={isLoading}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Status */}
        <div className="text-center text-sm">
          {!audioUrl && (
            <p className="text-muted-foreground">No audio loaded</p>
          )}
          {hasError && (
            <p className="text-destructive">❌ Audio failed to load</p>
          )}
          {!hasError && duration > 0 && (
            <p className="text-green-600">✅ Ready to play</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SimpleAudioFallback;